import {
  Button,
  Group,
  Loader,
  Paper,
  PinInput,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { useState } from 'react'
import { linkDeviceToLocation } from '../../../data/device.service'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCircleCheck,
  faCircleExclamation,
} from '@fortawesome/free-solid-svg-icons'
import { COLORS } from '../../../utils/constants'
import { OnboardingDevice } from '../../../models/Onboarding/OnboardingDevice'
import { useMsal } from '@azure/msal-react'

interface LinkDeviceStepProps {
  onDeviceLinked: (device: OnboardingDevice) => void
  isDeviceLinked: boolean
  locationId: number
}

function LinkDeviceStep(props: LinkDeviceStepProps) {
  const { onDeviceLinked, isDeviceLinked, locationId } = props
  const { instance } = useMsal()
  const [oneTimeCode, setOneTimeCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const validateOneTimeCode = async () => {
    setIsLoading(true)
    try {
      const device = await linkDeviceToLocation(
        oneTimeCode,
        locationId,
        instance,
      )
      setErrorMessage(null)
      if (device.id) {
        onDeviceLinked(device)
      }
    } catch (ex: any) {
      if (ex?.response?.status === 404) {
        setErrorMessage('Invalid code was provided')
      }
      if (ex?.response.status === 409) {
        setErrorMessage(
          'Device is linked to another location. Please unlink the device and try again.',
        )
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Paper
      shadow='md'
      withBorder
      bd={errorMessage ? `${COLORS.Error} solid` : '#dee2e6'}
      h={isDeviceLinked ? '300' : '60%'}
      radius={10}
      w='100%'
      maw={600}
      display='flex'
      style={{
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {isDeviceLinked ? (
        <Stack align='center' justify='flex-start' gap={30} h='100%' w='100%'>
          <Group
            bg={COLORS.Success}
            w='100%'
            h='30%'
            justify='center'
            style={{ borderTopLeftRadius: 10, borderTopRightRadius: 10 }}
          >
            <FontAwesomeIcon icon={faCircleCheck} color='white' fontSize={42} />
          </Group>
          <Title ml={8} mr={8} pl={24} pr={24}>
            Success!
          </Title>
          <Title ml={8} mr={8} size='h3' ta='center' pl={24} pr={24}>
            Your device was linked to your location
          </Title>
        </Stack>
      ) : (
        <Stack align='center' justify='center' gap={30} h={600}>
          <Title pl={24} pr={24}>
            Link your device
          </Title>
          <Title size='h4' ta='center' pl={24} pr={24}>
            Enter the 6 digit code shown on your device
          </Title>
          <PinInput
            error={!!errorMessage}
            oneTimeCode
            autoFocus
            placeholder={'0'}
            size='lg'
            length={6}
            value={oneTimeCode}
            fw={800}
            onChange={setOneTimeCode}
            onComplete={validateOneTimeCode}
            visibleFrom='sm'
          />
          <PinInput
            error={!!errorMessage}
            oneTimeCode
            autoFocus
            placeholder={'0'}
            size='sm'
            length={6}
            value={oneTimeCode}
            fw={800}
            onChange={setOneTimeCode}
            onComplete={validateOneTimeCode}
            hiddenFrom='sm'
          />
          {errorMessage && (
            <Group>
              <FontAwesomeIcon
                color={COLORS.Error}
                icon={faCircleExclamation}
              />
              <Text c={COLORS.Error}>{errorMessage}</Text>
            </Group>
          )}
          <Button
            size='lg'
            onClick={validateOneTimeCode}
            disabled={oneTimeCode.length !== 6 || isLoading}
            miw={100}
          >
            {isLoading ? <Loader type='dots' /> : 'Submit'}
          </Button>
        </Stack>
      )}
    </Paper>
  )
}

export default LinkDeviceStep
